import { useLiveQuery } from 'dexie-react-hooks'
import { useState } from 'react'
import { CampoBusca } from '../componentes/CampoBusca.js'
import { banco } from '../dados/banco.js'

/// Escolha de várias obras, para o cadastro de uma pessoa.
///
/// Com 65 obras, uma lista de caixas de marcar não cabe na tela de ninguém. Aqui
/// as escolhidas ficam à vista como etiquetas, e as outras só aparecem ao buscar.
///
/// A lista vem do aparelho: quem chega nesta tela é administrador, e administrador
/// enxerga todas as obras, então todas já estão baixadas.

export interface ObraResumo {
  id: string
  codigo: string
  nome: string
}

function normalizar(texto: string): string {
  return texto.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
}

export function EscolhaObras({
  selecionadas,
  aoMudar,
  conhecidas = [],
  desativado,
}: {
  selecionadas: string[]
  aoMudar: (ids: string[]) => void
  /// Obras que o servidor já mandou com nome — cobre uma obra desativada, que não
  /// está mais no aparelho mas continua no cadastro da pessoa.
  conhecidas?: ObraResumo[]
  desativado?: boolean
}) {
  const [busca, setBusca] = useState('')
  const locais = useLiveQuery(() => banco.obras.orderBy('codigo').toArray(), []) ?? []

  const porId = new Map<string, ObraResumo>()
  for (const o of conhecidas) porId.set(o.id, o)
  for (const o of locais) porId.set(o.id, o)

  const termo = normalizar(busca.trim())
  const resultados = termo
    ? locais
        .filter((o) => !selecionadas.includes(o.id))
        .filter((o) => normalizar(`${o.codigo} ${o.nome}`).includes(termo))
        .slice(0, 8)
    : []

  return (
    <div className="space-y-2">
      {selecionadas.length > 0 && (
        <ul className="flex flex-wrap gap-1">
          {selecionadas.map((id) => {
            const o = porId.get(id)
            return (
              <li key={id} className="etiqueta gap-1 bg-white text-slate-700 ring-1 ring-slate-200">
                {o ? `${o.codigo} · ${o.nome}` : 'Obra desconhecida'}
                {!desativado && (
                  <button
                    type="button"
                    onClick={() => aoMudar(selecionadas.filter((x) => x !== id))}
                    className="-mr-1 rounded-full px-1 text-slate-400 hover:text-alta"
                    aria-label={`Retirar ${o?.codigo ?? 'obra'}`}
                  >
                    ✕
                  </button>
                )}
              </li>
            )
          })}
        </ul>
      )}

      {!desativado && (
        <>
          <CampoBusca valor={busca} aoMudar={setBusca} placeholder="Buscar obra para adicionar" />
          {termo && (
            <ul className="overflow-hidden rounded-lg border border-slate-200 bg-white">
              {resultados.length === 0 ? (
                <li className="px-3 py-2 text-sm text-slate-500">Nenhuma obra encontrada.</li>
              ) : (
                resultados.map((o) => (
                  <li key={o.id}>
                    <button
                      type="button"
                      onClick={() => {
                        aoMudar([...selecionadas, o.id])
                        setBusca('')
                      }}
                      className="block w-full px-3 py-2 text-left text-sm hover:bg-marinho-50"
                    >
                      <span className="font-semibold text-marinho">{o.codigo}</span> · {o.nome}
                    </button>
                  </li>
                ))
              )}
            </ul>
          )}
          {locais.length === 0 && (
            <p className="text-xs text-slate-500">
              As obras ainda não foram baixadas neste aparelho. Volte à tela inicial com internet e
              tente de novo.
            </p>
          )}
        </>
      )}
    </div>
  )
}
